import * as React from 'react'
import { useEffect, useState } from 'react'
import { DropDiv, LabelButton, Paragraph } from './style'
import { connect } from 'react-redux'
import {
  displayToast,
  DocumentChecklistItem,
  DisplayToastDispatch,
  DocumentRecord,
  Organization,
  OrganizationError,
  OrganizationState,
  PageProps,
  Paths,
  User,
  setExpanded
} from '@logic'
import DocumentSection from '@modules/documents/components/document-section'
import Loading from 'react-loading'
import { ProgressBar } from '@components/progress'
import { History } from 'history'
import { BuiltInPermission } from '@helpers/constants'
import { withService } from '@src/shared/hocs/with-service'
import { NavigationProps, withNavigation } from '@components/navigation/navigation'
import { addLinkModal, newFolderModal } from '@modules/documents/components/modals'
import { DocumentChecklist } from '@modules/documents/components/document-checklist'
import { withLoadingCachedMultiple } from '@components/loading'
import { loadPermissions, PermissionProps } from '@components/permissions'
import { PageContent } from '@components/pages'
import { PageContentHeader } from '@components/pages/page-content-header'
import { withPermissions } from '@src/shared/hocs/with-permissions'
import { getQueryParams } from '@helpers/util'
import { CommonInput, FlexRow } from '@src/styles'
import { ActionsDrop } from '@shared/components/actionsDrop'
import Dropzone from 'react-dropzone'
import FolderUploadIcon from '@image/folder-upload.svg'
import FileUploadIcon from '@image/file-upload.svg'
import LinkAddIcon from '@image/link-add.svg'
import NewFolderIcon from '@image/folder-add.svg'
import { withTheme } from 'styled-components'
import { HttpClient } from '@src/service'
import { useGoogleDriveConnection } from '@modules/google-drive/hooks'
import { GoogleDriveAddModal } from '@modules/google-drive/components'

type BaseProps = PageProps & PermissionProps

interface Props extends NavigationProps, BaseProps {
  displayToast: DisplayToastDispatch
  history: History
  setExpanded: any
  service: HttpClient
}

interface StateProps {
  user: User
  currentOrganization: Organization
  organizationError?: OrganizationError
  organization?: OrganizationState
}
const LinkTemplate = `[InternetShortcut]
URL=$url
`

const withData = withLoadingCachedMultiple<Props>(loadPermissions) as any

const Documents = withTheme(withData(
  withPermissions([BuiltInPermission.viewDocuments])((props: Props & StateProps & any) => {
    const { currentOrganization, serviceOld, service, permissions, navigate, reload, setExpanded } = props
    const [documents, setDocuments] = useState<DocumentRecord[] | undefined>(undefined)
    const [uploadPercentageComplete, setUploadPercentageComplete] = useState<number | undefined>(undefined)
    const [loaded, setLoaded] = useState<boolean>(false)
    const [path, setPath] = useState<string[]>([])
    const fullPath = !currentOrganization?.legacyDataRoom ? encodeURIComponent('/' + path.join('/'))
      : encodeURIComponent(path.join('/') || '/')
    const canEditDocument = !!permissions.find((permission: any) => permission.id === BuiltInPermission.editDocuments)
    const query = getQueryParams()

    const [checklistRefreshKey, setChecklistRefreshKey] = useState(0)
    const [driveModalOpen, setDriveModalOpen] = useState(false)
    const [driveChecklistItem, setDriveChecklistItem] = useState<DocumentChecklistItem | undefined>(undefined)
    const currentDataRoomPath = !currentOrganization?.legacyDataRoom
      ? '/' + path.join('/')
      : path.join('/') || '/'
    const normalizedDataRoomPath = currentDataRoomPath === '' ? '/' : currentDataRoomPath
    const googleDrive = useGoogleDriveConnection({
      httpClient: service,
      organizationId: currentOrganization.id
    })

    useEffect(() => {
      const handleOAuthCallback = async (event: MessageEvent) => {
        if (event.origin !== window.location.origin) return
        if (event.data?.type === 'google-drive-oauth-callback' && event.data?.code) {
          try {
            await googleDrive.completeConnect(event.data.code)
          } catch (err) {
            console.error('OAuth callback error:', err)
          }
        }
      }

      window.addEventListener('message', handleOAuthCallback)
      return () => window.removeEventListener('message', handleOAuthCallback)
    }, [googleDrive.completeConnect])

    const handleLinkCreate = async (props: any) => {
      if (props) {
        const link = LinkTemplate.replace('$url', props.path!)
        const parts = [new Blob([link], { type: 'text/plain' })];
        const fileName = props.name! + '.url'
        const file = new File(parts, fileName, {
          lastModified: Date.now(),
        })
        await uploadDocuments([file])
        addALinkModal.setVisible(false)
      }
    }
    const addALinkModal = addLinkModal(handleLinkCreate)

    const openDriveModal = (item?: DocumentChecklistItem) => {
      setDriveChecklistItem(item)
      setDriveModalOpen(true)
    }

    const closeDriveModal = () => {
      setDriveModalOpen(false)
      setDriveChecklistItem(undefined)
    }

    const refreshAfterDriveLink = async () => {
      const records = await getDocuments()
      setDocumentsAndLoaded(records)
      setChecklistRefreshKey(prev => prev + 1)
      reload()
    }

    const createFolder = async (values: { folderName: string }) => {
      const result = (await serviceOld.addFolder(currentOrganization.id, {
        path: !currentOrganization?.legacyDataRoom ? '/' + path.join('/') :
          path.join('/') || '/',
        folderName: values.folderName.trim(),
      })) as any
      if (!result.error) {
        folderModal.setVisible(false)
        const records = await getDocuments()
        setDocumentsAndLoaded(records)
      }
    }

    const folderModal = newFolderModal(createFolder)

    const getDocuments = async (shouldSetDocuments?: boolean) => {
      setLoaded(false)
      if (currentOrganization) {
        const records = await serviceOld.getOrganizationDocuments(currentOrganization.id, fullPath)
        const recordsValue = records?.map((r: any) => {
          if (r.folder) {
            return {
              ...r, items: r.folder.childCount == 1 ? `${r.folder.childCount} item`
                :
                `${r.folder.childCount} items`
            }
          }
          return r
        })
        if (shouldSetDocuments) {
          setDocumentsAndLoaded(recordsValue)
        }
        return recordsValue
      }
    }

    const setDocumentsAndLoaded = (records: DocumentRecord[] | undefined) => {
      setDocuments(records)
      setLoaded(true)
    }

    useEffect(() => {
      if (query.path && query.path !== '/') {
        setPath(query.path.split(','))
      }
    }, [])

    useEffect(() => {
      let mounted = true
        ; (async () => {
          const records = await getDocuments()
          if (mounted) {
            setDocumentsAndLoaded(records)
            const fullPath = encodeURIComponent(path.join('/') || '/')
            const search = `path=${fullPath}`
            navigate({ pathname: Paths.organizationDataRoom, search }, { organization: currentOrganization.id })
          }
        })()
      return () => {
        mounted = false
      }
    }, [path])

    const uploadDocuments = async (files: any) => {
      const onUploadProgress = (event: any) => {
        if (event.lengthComputable) {
          const percentages = +((event.loaded / event.total) * 100).toFixed(2)
          setUploadPercentageComplete(percentages)
        }
      }
      await serviceOld.uploadOrganizationDocuments(files, currentOrganization.id, fullPath, onUploadProgress)
      setUploadPercentageComplete(undefined)
      setExpanded()
      const records = await getDocuments()
      setDocumentsAndLoaded(records)
      setChecklistRefreshKey(prev => prev + 1)
      reload()
    }

    const renderPath = () => {
      const all = ['Data Room'].concat(path)
      const links = all.slice(0, -1) // Every token except the last
      const last = all[all.length - 1]
      return (
        <div>
          {links.map((t, i) => (
            <span key={i}>
              <a
                href={''}
                onClick={e => {
                  e.preventDefault()
                  setPath(path.slice(0, i))
                }}
              >
                {t}
              </a>
              <span> {'>'} </span>
            </span>
          ))}
          <span>{last}</span>
        </div>
      )
    }
    const dropOptions = () => {
      const uploadFolder = () => {
        return <Dropzone onDrop={async (files: any) => {
          return uploadDocuments(files)
        }}>
          {({ getInputProps }) => (
            <LabelButton>
              <input {...getInputProps()} directory="" webkitdirectory="" type="file" />
              <FolderUploadIcon />
              <span>Upload Folder</span>
            </LabelButton>
          )}
        </Dropzone>

      }

      const uploadFile = () => {
        return <Dropzone onDrop={async (files: any) => {
          return uploadDocuments(files)
        }}>
          {({ getInputProps }) => (
            <LabelButton>
              <CommonInput {...getInputProps()} />
              <FileUploadIcon />
              <span>Upload File</span>
            </LabelButton>
          )}
        </Dropzone>

      }
      if (canEditDocument) {
        return [
          [<DropDiv>
            <NewFolderIcon />
            <span>New Folder</span>
          </DropDiv>, () => folderModal.setVisible(true)],
          [uploadFolder(), () => { /* noop */ }],
          [uploadFile(), () => { /* noop */ }],
          [<DropDiv>
            <LinkAddIcon />
            <span>Add Link</span>
          </DropDiv>, () => addALinkModal.setVisible(true)],
          [<DropDiv>
            <LinkAddIcon />
            <span>Add from Google Drive</span>
          </DropDiv>, () => openDriveModal()]
        ]
      } else return []
    }

    return (
      <>
        {folderModal.component}
        {addALinkModal.component}
        <GoogleDriveAddModal
          isOpen={driveModalOpen}
          defaultDataRoomPath={normalizedDataRoomPath}
          isConnected={googleDrive.connection?.connected || false}
          connectedEmail={googleDrive.connection?.email}
          checklistKey={driveChecklistItem?.key}
          checklistLabel={driveChecklistItem?.label}
          onClose={closeDriveModal}
          onConnect={googleDrive.initiateConnect}
          onResolveUrl={googleDrive.resolveUrl}
          onLinkItems={async (items, targetPath, mirrorStructure, checklistKey) => {
            await googleDrive.linkItems(items, targetPath, mirrorStructure, checklistKey)
            await refreshAfterDriveLink()
          }}
          onImportItems={async (items, targetPath, mirrorStructure, checklistKey) => {
            await googleDrive.importItems(items, targetPath, mirrorStructure, checklistKey)
            await refreshAfterDriveLink()
          }}
          onSyncFolders={async (folders, targetPath, syncSubfolders) => {
            await Promise.all(folders.map(folder => googleDrive.addSyncConfig(folder.id, folder.name || folder.id, targetPath, syncSubfolders)))
            await refreshAfterDriveLink()
          }}
        />
        <PageContent>
          <PageContentHeader title={renderPath()} direction>
            <FlexRow justifyContent={'flex-end'} style={{ marginLeft: 'auto' }}>
              <ActionsDrop options={dropOptions()} />
            </FlexRow>
          </PageContentHeader>
          <div>
            <ProgressBar percentage={uploadPercentageComplete} />
            <hr />
            <Paragraph>
              Upload all your files in one place for everyone in your organization to see and share. No more digging around to find
              important files, with Equa Document Manager we will keep your document sharing and organization simplified!
            </Paragraph>
            <DocumentChecklist
              key={checklistRefreshKey}
              organizationId={currentOrganization.id}
              documents={documents}
              canEdit={canEditDocument}
              defaultExpanded={!documents || documents.length === 0 && path.length === 0}
              onLinkFromDrive={item => openDriveModal(item)}
            />
            {!loaded ? (
              <Loading />
            ) : (
              <>
                {documents && (
                  <DocumentSection
                    setLoaded={setLoaded}
                    getDocuments={getDocuments}
                    setDocumentsAndLoaded={setDocumentsAndLoaded}
                    canEditDocument={canEditDocument}
                    uploadDocuments={uploadDocuments}
                    path={path}
                    setPath={setPath}
                    documents={documents}
                    theme={props.theme!}
                    reload={reload}
                    onAddFromDrive={() => openDriveModal()}
                  />
                )}
              </>
            )}
          </div>
        </PageContent>
      </>
    )
  })
))

const mapStateToProps = (state: any) => {
  return {
    user: state.user,
    organizationError: state.organization!.organizationError,
    currentOrganization: state.organization!.currentOrganization,
  }
}

// Lazy HOC initialization — defers withService(Documents) to first render.
// This avoids a webpack scope-hoisting temporal dead zone: in production mode,
// webpack concatenates with-service.tsx into the same module chunk as documents.tsx,
// and in the concatenated order the DataRoomPage assignment was reached before
// withService was assigned, causing `(void 0)(Documents)` → TypeError at bundle init.
// By deferring to render time, all module-level assignments are complete.
let _DataRoomPage: React.ComponentType<any> | null = null
export const DataRoomPage: React.ComponentType<any> = (props: any) => {
  if (!_DataRoomPage) {
    _DataRoomPage = withNavigation(connect(mapStateToProps, { displayToast, setExpanded })(withService(Documents)))
  }
  return React.createElement(_DataRoomPage, props)
}
